// Small local UI kit (no component library). Accessibility is wired in here once:
// labels, aria-describedby for hints/errors, role="alert" for errors (UI-A11Y-1).
// u16: the look follows the ai-audit-firm reference (rounded-xl bordered cards, rounded-lg controls,
// icon tiles, accent focus rings) on semantic tokens, so the dark theme needs no changes here.
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  LayoutTemplate,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import {
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { th } from '../i18n/th';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-on-accent hover:bg-accent-hover',
  secondary: 'border border-line-strong bg-surface text-ink hover:bg-surface-muted',
  danger: 'bg-danger-solid text-on-accent hover:bg-danger-solid-hover',
  ghost: 'text-accent-ink hover:bg-accent-soft',
};

export function Button({
  variant = 'primary',
  loading = false,
  icon: Icon,
  className = '',
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  loading?: boolean;
  /** Decorative leading icon (the text label stays). */
  icon?: LucideIcon | undefined;
}) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60 ${VARIANTS[variant]} ${className}`}
    >
      {loading ? <Spinner small /> : Icon ? <Icon className="h-4 w-4" aria-hidden /> : null}
      {children}
    </button>
  );
}

const FIELD =
  'w-full rounded-lg border bg-surface px-3 py-2.5 text-ink placeholder:text-ink-subtle transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40';

export function TextField({
  label,
  hint,
  error,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        {...input}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={`${FIELD} ${error ? 'border-danger' : 'border-line-strong'}`}
      />
      {hint && (
        <p id={hintId} className="text-sm text-ink-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/** A multi-line TextField; with `maxLength` it also shows how much room is left. */
export function TextArea({
  label,
  hint,
  error,
  value,
  maxLength,
  ...input
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  value: string;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-sm font-medium text-ink">
          {label}
        </label>
        {maxLength !== undefined && (
          <span className="text-xs text-ink-subtle" aria-hidden>
            {value.length}/{maxLength}
          </span>
        )}
      </div>
      <textarea
        id={id}
        rows={4}
        {...input}
        value={value}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={`${FIELD} ${error ? 'border-danger' : 'border-line-strong'}`}
      />
      {hint && (
        <p id={hintId} className="text-sm text-ink-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/** 6-digit numeric code entry with OS one-time-code autofill. */
export function CodeInput(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
  testId: string;
}) {
  return (
    <TextField
      label={props.label}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      error={props.error}
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      pattern="[0-9]{6}"
      data-testid={props.testId}
    />
  );
}

const ALERT = {
  error: { cls: 'border-danger/40 bg-danger-soft text-danger', Icon: XCircle },
  success: { cls: 'border-success/40 bg-success-soft text-success', Icon: CheckCircle2 },
  info: { cls: 'border-accent/40 bg-accent-soft text-accent-ink', Icon: Info },
  warning: { cls: 'border-warning/40 bg-warning-soft text-warning', Icon: AlertTriangle },
} as const;

export function Alert({
  tone = 'error',
  children,
}: {
  tone?: 'error' | 'success' | 'info' | 'warning';
  children: ReactNode;
}) {
  const { cls, Icon } = ALERT[tone];
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${cls}`}
    >
      <Icon className="mt-1 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** A tinted square holding an icon (cards, stats, empty states, auth screens). */
export function IconTile({ icon: Icon, size = 'md' }: { icon: LucideIcon; size?: 'md' | 'lg' }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center bg-accent-soft text-accent-ink ${size === 'lg' ? 'h-16 w-16 rounded-2xl' : 'h-10 w-10 rounded-lg'}`}
      aria-hidden
    >
      <Icon className={size === 'lg' ? 'h-8 w-8' : 'h-5 w-5'} />
    </span>
  );
}

export function Card({
  title,
  icon,
  children,
  actions,
}: {
  title?: string;
  icon?: LucideIcon | undefined;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-line bg-surface p-5 transition-colors sm:p-6">
      {(title || actions) && (
        <div className="mb-4 flex items-center justify-between gap-4">
          {title && (
            <h2 className="flex items-center gap-2 text-lg font-semibold text-ink">
              {icon && <IconTile icon={icon} />}
              {title}
            </h2>
          )}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/** Dashboard-style figure: label, value, optional note and icon tile. */
export function StatCard({
  label,
  value,
  note,
  icon,
  testId,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  icon?: LucideIcon | undefined;
  testId?: string;
}) {
  return (
    <div
      className="flex items-start justify-between gap-3 rounded-xl border border-line bg-surface p-5"
      data-testid={testId}
    >
      <div className="min-w-0">
        <p className="text-sm text-ink-muted">{label}</p>
        <p className="mt-1 text-2xl font-bold text-ink">{value}</p>
        {note && <p className="mt-1 text-sm text-ink-muted">{note}</p>}
      </div>
      {icon && <IconTile icon={icon} />}
    </div>
  );
}

const PILL = {
  neutral: 'bg-surface-muted text-ink-muted',
  accent: 'bg-accent-soft text-accent-ink',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
} as const;

export function StatusPill({
  tone = 'neutral',
  children,
}: {
  tone?: keyof typeof PILL;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-medium ${PILL[tone]}`}
    >
      {children}
    </span>
  );
}

export function Spinner({ small = false }: { small?: boolean }) {
  return (
    <span
      role="status"
      aria-label={th.app.loading}
      className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${small ? 'h-4 w-4' : 'h-8 w-8 text-accent-ink'}`}
    />
  );
}

export function EmptyState({
  title,
  help,
  action,
  icon = LayoutTemplate,
}: {
  title: string;
  help: string;
  action?: ReactNode;
  icon?: LucideIcon;
}) {
  return (
    <div className="flex flex-col items-center rounded-xl border border-dashed border-line-strong bg-surface p-10 text-center">
      <IconTile icon={icon} />
      <h2 className="mt-3 text-lg font-semibold text-ink">{title}</h2>
      {help && <p className="mt-1 text-ink-muted">{help}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Native <dialog>: focus trapping and Escape come from the browser. */
export function Dialog({
  open,
  title,
  onClose,
  children,
  size = 'md',
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** u46: `full` for things that need the screen (a website preview). */
  size?: 'md' | 'full';
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      aria-labelledby={titleId}
      className={`m-auto rounded-2xl border border-line bg-surface text-ink backdrop:bg-overlay/60 ${size === 'full' ? 'flex h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-none flex-col p-4 sm:p-6 [&:not([open])]:hidden' : 'w-full max-w-md p-6'}`}
    >
      <h2 id={titleId} className="mb-4 text-lg font-semibold">
        {title}
      </h2>
      {children}
    </dialog>
  );
}

/** Signed-out screens (login, register, 2FA, reset, verify): a centred card, no sidebar. */
export function PageShell({
  title,
  icon = LayoutTemplate,
  children,
}: {
  title: string;
  icon?: LucideIcon;
  children: ReactNode;
}) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mb-4 flex justify-center">
            <IconTile icon={icon} size="lg" />
          </div>
          <p className="text-2xl font-bold text-ink">{th.app.name}</p>
          <h1 className="mt-1 text-lg text-ink-muted">{title}</h1>
        </div>
        <div className="flex flex-col gap-6 rounded-2xl border border-line bg-surface p-6 sm:p-8">
          {children}
        </div>
        <p className="mt-6 text-center text-sm text-ink-muted">
          {th.app.name} © {new Date().getFullYear()}
        </p>
      </div>
    </main>
  );
}
